
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UpdateSessionData {
  subscriptionId: string;
  sessionsUsed: number;
}

export const useUpdateClientSessions = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (updates: UpdateSessionData[]) => {
      console.log('Starting session updates mutation with:', updates);

      const updatePromises = updates.map(async ({ subscriptionId, sessionsUsed }) => {
        console.log(`Updating subscription ${subscriptionId} with ${sessionsUsed} sessions`);
        
        const result = await supabase
          .from('client_subscriptions')
          .update({
            current_cycle_sessions_used: sessionsUsed,
            updated_at: new Date().toISOString()
          })
          .eq('id', subscriptionId)
          .select('*');

        console.log(`Update result for subscription ${subscriptionId}:`, result);
        
        if (result.error) {
          console.error(`Error updating subscription ${subscriptionId}:`, result.error);
          throw new Error(`Failed to update subscription ${subscriptionId}: ${result.error.message}`);
        }

        if (!result.data || result.data.length === 0) {
          console.error(`No data returned for subscription ${subscriptionId}`);
          throw new Error(`No subscription found with ID ${subscriptionId}`);
        }

        console.log(`Successfully updated subscription ${subscriptionId}:`, result.data[0]);
        return result.data[0];
      });

      const results = await Promise.all(updatePromises);
      console.log('All session updates completed:', results);
      
      // Add a longer delay to ensure database triggers have completed processing
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return results;
    },
    onSuccess: async (results) => {
      console.log('Session update mutation succeeded:', results);
      
      // Get the client ID from the first result to invalidate client-specific queries
      const clientId = results[0]?.client_id;
      
      if (clientId) {
        console.log('Invalidating queries for client:', clientId);
        
        // Invalidate all client-related queries with more specific targeting
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['clients'] }),
          queryClient.invalidateQueries({ queryKey: ['client', clientId] }),
          queryClient.invalidateQueries({ queryKey: ['unpaid-sessions', clientId] }),
          queryClient.invalidateQueries({ queryKey: ['client-sessions', clientId] }),
          queryClient.invalidateQueries({ queryKey: ['clientPayments', clientId] }),
          queryClient.invalidateQueries({ queryKey: ['clientSubscriptions', clientId] }),
        ]);
        
        // Force immediate refetch of critical data with stale time override
        await Promise.all([
          queryClient.refetchQueries({ 
            queryKey: ['client', clientId],
            type: 'active'
          }),
          queryClient.refetchQueries({ 
            queryKey: ['clients'],
            type: 'active'
          }),
          queryClient.refetchQueries({ 
            queryKey: ['unpaid-sessions', clientId],
            type: 'active'
          }),
        ]);

        // Update the client data in cache to ensure immediate UI refresh
        queryClient.setQueryData(['client', clientId], (oldData: any) => {
          if (oldData) {
            console.log('Updating client cache data with latest subscription info');
            const updatedSubscriptions = oldData.subscriptions?.map((sub: any) => {
              const update = results.find(r => r.id === sub.id);
              return update ? { ...sub, currentCycleSessionsUsed: update.current_cycle_sessions_used } : sub;
            });
            
            return {
              ...oldData,
              subscriptions: updatedSubscriptions || oldData.subscriptions,
              // Force a timestamp update to trigger re-renders
              updated_at: new Date().toISOString()
            };
          }
          return oldData;
        });
      }
      
      console.log('All queries invalidated and refetched, UI should refresh with updated payment status');
    },
    onError: (error) => {
      console.error('Session update mutation failed:', error);
    }
  });
};
